import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  input,
  output,
  signal,
  viewChild
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from 'auth';
import { Conference } from 'calendar';
import { I18nService } from 'i18n';
import { ConferenceMedia } from 'media';
import { defer, delayWhen, expand, lastValueFrom, repeatWhen, switchMap, timer } from "rxjs";
import { Logger, User } from 'shared';
import { JitsiService } from '../jitsi.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

declare var JitsiMeetExternalAPI: any;

// https://github.com/jitsi/jitsi-meet/blob/master/config.js
// https://github.com/jitsi/jitsi-meet/blob/master/interface_config.js
// https://github.com/jitsi/jitsi-meet/blob/master/modules/API/external/external_api.js
// https://github.com/jitsi/jitsi-meet/blob/master/react/features/base/util/uri.ts

// Custom toolbar buttons
// https://jitsi.github.io/handbook/docs/dev-guide/dev-guide-configuration#customtoolbarbuttons

@Component({
  selector: 'conference-jitsi',
  templateUrl: './conference-jitsi.component.html',
  styleUrls: ['./conference-jitsi.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ConferenceJitsiComponent {

  // media = input.required<ConferenceMedia | undefined>();

  media = input<ConferenceMedia | undefined>();
  conference = input<Conference | undefined>();
  conference$ = computed<Conference | undefined>(() => {
    return this.conference() || this.media()?.properties?.['conference'];
  });
  iframe = viewChild.required<ElementRef<HTMLIFrameElement>>('iframe');
  onClose = output<void>();

  protected participants: { id: string, displayName: string, role?: string }[] = [];

  protected isViewInitialized = signal(false);
  protected api: any;
  protected defaultOptions: any;
  protected logger = new Logger('ConferencePanelJitsiComponent');

  constructor(
    protected jitsiService: JitsiService,
    protected authService: AuthService,
    protected route: ActivatedRoute,
    protected i18nService: I18nService,
    protected destroyRef: DestroyRef) {
    effect(() => {
      const conference = this.conference$();
      if (this.isViewInitialized() && conference) {
        this.startConference(conference);
      }
    });
  }

  ngOnInit(): void {
    // https://github.com/jitsi/jitsi-meet/blob/master/config.js
    // https://github.com/jitsi/jitsi-meet/blob/master/interface_config.js
    this.defaultOptions = {
      width: '100%',
      height: '100%',
      parentNode: undefined,
      configOverwrite: {
        // prejoinPageEnabled: false, - deprecated use prejoinCnfig
        prejoinConfig: {
          enabled: false
        },
        startWithAudioMuted: true,
        startWithVideoMuted: true,
        subject: "Meeting",
        disableInviteFunctions: true,
        enableClosePage: false
      },
      interfaceConfigOverwrite: {
        APP_NAME: "MNLO",
        MOBILE_APP_PROMO: false,
        // DEFAULT_BACKGROUND: 'var(--color-primary)',
        // RECENT_LIST_ENABLED: false,
        SHOW_JITSI_WATERMARK: false,
        SHOW_BRAND_WATERMARK: false,
        SHOW_PROMOTIONAL_CLOSE_PAGE: false,
        TOOLBAR_BUTTONS: [
          'microphone', 'camera', 'desktop', 'hangup',
        ]
      },
      // jwt: "",
      onload: "",
      invitees: [],
      devices: {},
      // userInfo: { displayName:  this.authService.getUser()?.full_name },
      // lang: current language
    };
  }

  ngAfterViewInit(): void {
    this.isViewInitialized.set(true);
  }

  protected async startConference(conference: Conference) {
    const user = this.authService.getUser();
    const options = JSON.parse(JSON.stringify(this.defaultOptions)); //deep clone
    options.roomName = conference.properties?.room_name || conference.name; //conference.joinLink;
    options.parentNode = this.iframe().nativeElement;
    options.lang = this.i18nService.getLanguage();
    // options.configOverwrite = { ...options.configOverwrite,
    //   startWithAudioMuted: this.data.audioMuted,
    //   startWithVideoMuted: this.data.videoMuted,
    // }
    options.configOverwrite.prejoinConfig.enabled = !user;
    try {
      if (user) {
        options.jwt = await this.jitsiService.createJwt(conference);
        options.userInfo = { displayName: user?.full_name };
      }
      // this.logger.debug("OPTIONS", options);
      // this.iframe().nativeElement.style.position = 'absolute';
      await lastValueFrom(this.jitsiService.load());

      this.api = new JitsiMeetExternalAPI(`${this.jitsiService.domain}`, options);
      const properties = conference?.properties;
      const moderator = !!user && (user.id == properties?.hostId || properties?.moderators?.includes(user.username));
      const maxParticipants = conference.properties?.max_participants ?? null;
      this.attachListeners(this.api, moderator, maxParticipants);
      //  Sample result of getRoomsInfo:
      //  {
      //      "rooms": [
      //          {
      //              "isMainRoom": true,
      //              "id": "bunny@conference.meet.mnlo.app",
      //              "jid": "bunny@conference.meet.mnlo.app/60328672",
      //              "participants": [
      //                  {
      //                      "role": "moderator",
      //                      "displayName": "Mihail Bratoev",
      //                      "avatarUrl": "",
      //                      "id": "60328672"
      //                  }
      //              ]
      //          }
      //      ]
      //  }
      if (moderator) {
        const kick = async () => {
          const roomsInfo = await this.api.getRoomsInfo()
          this.logger.debug('RoomsInfo', roomsInfo);
          const mainRoom = roomsInfo.rooms.find((r: any) => r.isMainRoom);
          const kickIds =
            mainRoom && mainRoom.participants?.length > maxParticipants
              ? mainRoom.participants
                .filter((p: any) => p.role !== 'moderator')
                .slice(-(mainRoom.participants.length - maxParticipants))
                .map((p: any) => p.id)
              : [];
          this.logger.debug('Kick participant ids:', kickIds);
          for (const id of kickIds) {
            try {
              this.logger.debug(`Kicking participant ${id}...`);
              await this.api.executeCommand('kickParticipant', id);
              this.logger.debug(`Participant ${id} kicked successfully`);
            } catch (error) {
              this.logger.error(`Failed to kick participant ${id}:`, error);
            }
          }
        }
        const cleanup$ = defer(async () => kick())
          .pipe(
            expand(result =>
              timer(2000).pipe(
                switchMap(async () => kick())
              )
            ),
            takeUntilDestroyed(this.destroyRef)
          );
        cleanup$.subscribe();
      }
    } catch (error) {
      this.logger.error('Failed to start conference: ' + error);
    }
  }

  protected attachListeners(api: any, moderator: boolean, maxParticipants: number | null) {
    let localParticipantId: string | null = null;
    api.addEventListeners({
      readyToClose: (event: any) => {
        this.logger.debug('Ready to close', event);
        this.close();
      },
      participantJoined: (participant: { id: string, displayName: string }) => {
        // not called for local participant
        this.logger.debug('Participant joined:', participant);
        this.participants.push(participant);
        if (moderator) {
          const kick = () => maxParticipants && this.participants.length >= maxParticipants;
          if (kick()) {
            setTimeout(() => {
              if (kick()) {
                this.logger.debug('kickParticipant', participant);
                // api.executeCommand('kickParticipant', participant.id);
              }
            }, 1000);
          }
        }
      },
      participantLeft: (participant: { id: string }) => {
        this.logger.debug('Participant left:', participant.id)
        this.participants = this.participants.filter(p => p.id != participant.id)
      },
      videoConferenceJoined: async (participant: { id: string, displayName: string }) => {
        this.logger.debug('Local participant joined:', participant);
        localParticipantId = participant.id;
        this.participants.push(participant);
      },
      videoConferenceLeft: () => {
        this.logger.info('Local participant left');
        this.participants = this.participants.filter(p => p.id != localParticipantId)
        this.close();
      },
      participantRoleChanged: (event: { id: string, role: string }) => {
        const participant = this.participants.find(p => p.id = event.id);
        if (participant) participant.role = event.role;
        if(event.role === 'moderator') {
        }
      }
      // audioMuteStatusChanged: (event: any) => this.logger.debug('Audio mute status changed:', event),
      // videoMuteStatusChanged: (event: any) => this.logger.debug('Video mute status changed:', event)
    })
  }
  // @HostListener('swipedown', ['$event'])
  close(event?: Event) {
    event?.preventDefault();
    this.onClose.emit();
  }

  toggleAudio(): void {
    this.api?.executeCommand('toggleAudio');
  }

  toggleVideo(): void {
    this.api?.executeCommand('toggleVideo');
  }
}